import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { select, Store } from '@ngrx/store';
import * as validate from '../state';
import { validateLink, validate as validateAction } from '../state';
import * as login from '@app/auth/routes/login/state';
import { selectAuthIdentity, selectRecoveryCodes } from '@app/auth/core/state/auth.selectors';
import { TwoFACode } from '@app/auth/core/state/auth.model';
import {
  authClearConfigure2FAState,
  authRedirectAfterLogin,
  authSetup2FAAndLogin,
} from '@app/auth/core/state/auth.actions';
import { map, take, takeUntil, tap } from 'rxjs/operators';
import { TwoFactorSetupService } from '@app/shared/services/two-factor-setup.service';
import { Subject } from 'rxjs';

@Component({
  selector: 'tc-validate-page',
  templateUrl: './validate-page.component.html',
  styleUrls: ['./validate-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TwoFactorSetupService],
})
export class ValidatePageComponent implements OnDestroy {
  public pending$ = this.store$.pipe(select(validate.select.pending));
  public error$ = this.store$.pipe(select(validate.select.error));
  public isLinkValid$ = this.store$.pipe(select(validate.select.isLinkValid));
  public value$ = this.route.params;

  public verify2FAError$ = this.store$.pipe(select(login.select.verify2FAError));
  public identity$ = this.store$.pipe(select(selectAuthIdentity));
  public backupCodes$ = this.store$.pipe(select(selectRecoveryCodes));
  private destroyed$ = new Subject<void>();

  constructor(
    private store$: Store<validate.State>,
    private route: ActivatedRoute,
    private twoFService: TwoFactorSetupService,
  ) {
    this.route.params
      .pipe(
        take(1),
        tap(({ identityValidationToken, email }) => {
          this.store$.dispatch(validateLink({ payload: { identityValidationToken, email } }));
        }),
      )
      .subscribe();
    this.initTwoFASetup();
  }

  public onValidateIdentity($event: validate.ValidateIdentity): void {
    this.store$.dispatch(validateAction({ payload: $event }));
  }

  onEnable2FA($event: TwoFACode): void {
    this.store$.dispatch(authSetup2FAAndLogin($event));
  }

  onClearRecoveryAndGoInside(): void {
    this.store$.dispatch(authClearConfigure2FAState());
    this.store$.dispatch(authRedirectAfterLogin({ path: '' }));
  }

  initTwoFASetup(): void {
    this.twoFService.backupCodes$
      .pipe(
        map(codes => {
          return !!codes?.length;
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe(value => {
        this.twoFService.emitLastStep(value);
      });

    this.twoFService.onReset$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.onClearRecoveryAndGoInside();
    });

    this.twoFService.onEnable$.pipe(takeUntil(this.destroyed$)).subscribe(value => {
      this.onEnable2FA(value);
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
